import { useEffect, useRef, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';

import { confirmEmail } from './authApi.ts';

type Status = 'confirming' | 'confirmed' | 'failed' | 'missing-parameters';

/**
 * Where the link in the confirmation email lands.
 *
 * The token itself is verified by the API; this page exists so that clicking the link
 * produces something that looks like the app rather than a bare line of plain text from
 * a framework endpoint.
 */
export function ConfirmEmailPage() {
  const [searchParams] = useSearchParams();

  const userId = searchParams.get('userId');
  const code = searchParams.get('code');

  // A malformed link is knowable from the URL alone, so it is the initial state rather
  // than something an effect discovers and then sets — no cascading render.
  const [status, setStatus] = useState<Status>(() =>
    userId && code ? 'confirming' : 'missing-parameters',
  );

  // React 19 StrictMode runs effects twice in development. The confirmation endpoint is
  // idempotent so a double call is harmless, but guarding keeps the UI from flickering.
  const attempted = useRef(false);

  useEffect(() => {
    if (attempted.current || !userId || !code) {
      return;
    }
    attempted.current = true;

    confirmEmail(userId, code).then(
      () => setStatus('confirmed'),
      () => setStatus('failed'),
    );
  }, [userId, code]);

  return (
    <main className="app-shell app-shell--centred">
      <section className="card stack" aria-live="polite">
        {status === 'confirming' && (
          <>
            <h2>Confirming…</h2>
            <p className="muted">One moment.</p>
          </>
        )}

        {status === 'confirmed' && (
          <>
            <h2>You&rsquo;re all set</h2>
            <p>Your email address is confirmed.</p>
            <Link className="button button--primary" to="/login">
              Sign in
            </Link>
          </>
        )}

        {status === 'failed' && (
          <>
            <h2>That link didn&rsquo;t work</h2>
            <p>
              Confirmation links expire after 24 hours and can look broken if an email client
              wrapped the address across two lines.
            </p>
            <p className="muted">
              Try signing in — if the address still needs confirming, you can send yourself a
              fresh link from there.
            </p>
            <Link className="button button--quiet" to="/login">
              Back to sign in
            </Link>
          </>
        )}

        {status === 'missing-parameters' && (
          <>
            <h2>Something is missing from that link</h2>
            <p className="muted">
              It looks like only part of the address was opened. Copy the whole link from the
              email, including everything after the question mark.
            </p>
            <Link className="button button--quiet" to="/login">
              Back to sign in
            </Link>
          </>
        )}
      </section>
    </main>
  );
}
